import { Outlet, NavLink, useNavigate, Link, useLocation } from "react-router-dom";
import { useState } from "react";
import { LayoutGrid, Briefcase, Users, ClipboardCheck, ShieldCheck, BarChart2, Settings as SettingsIcon, LogOut, Search, Sparkles, Mic, CalendarDays, LogOut as ExitIcon, ShieldPlus } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import AlfredPanel from "@/components/AlfredPanel";
import RecruitSubNav from "@/components/RecruitSubNav";
import Logo from "@/components/Logo";
import NotificationsBell from "@/components/NotificationsBell";
import CommandPalette from "@/components/CommandPalette";
import NotificationBanner from "@/components/NotificationBanner";
import { useEffect } from "react";

const NAV = [
  { to: "/app/dashboard", label: "Dashboard", icon: LayoutGrid, min: 0 },
  { to: "/app/recruit/jobs", label: "Recruit", icon: Briefcase, min: 1 },
  { to: "/app/recruit/talent", label: "Talent", icon: Search, min: 2 },
  { to: "/app/interviews", label: "Interviews", icon: CalendarDays, min: 1 },
  { to: "/app/calendar", label: "Calendar", icon: CalendarDays, min: 1 },
  { to: "/app/people", label: "People", icon: Users, min: 1 },
  { to: "/app/hr", label: "HR & workforce", icon: Users, min: 3 },
  { to: "/app/onboarding", label: "Onboarding", icon: ClipboardCheck, min: 3 },
  { to: "/app/offboarding", label: "Offboarding", icon: ExitIcon, min: 3 },
  { to: "/app/compliance", label: "Compliance", icon: ShieldCheck, min: 3 },
  { to: "/app/compliance-plus", label: "Compliance+", icon: ShieldPlus, min: 3 },
  { to: "/app/analytics", label: "Analytics", icon: BarChart2, min: 3 },
  { to: "/app/settings", label: "Settings", icon: SettingsIcon, min: 3 },
];

const ROLE_LEVELS = {
  CANDIDATE: 0, EMPLOYEE: 0, HIRING_MANAGER: 1, RECRUITER: 2, HR_ADMIN: 3, SUPER_ADMIN: 4, PLATFORM_OWNER: 5,
};

export default function AppShell() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const level = ROLE_LEVELS[user?.role] ?? 0;
  const [askOpen, setAskOpen] = useState(false);
  const [cmdOpen, setCmdOpen] = useState(false);
  const showRecruitNav = location.pathname.startsWith("/app/recruit/");

  // Global Ctrl/⌘+K shortcut to open the command palette
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setCmdOpen(o => !o);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const doLogout = async () => { await logout(); nav("/login"); };

  const initials = (user?.name || user?.email || "?").split(" ").map(x => x[0]).slice(0,2).join("").toUpperCase();

  return (
    <div className="min-h-screen flex bg-[var(--app-bg)]">
      {/* Sidebar */}
      <aside className="sidebar-dark w-60 shrink-0 hidden lg:flex flex-col" data-testid="app-sidebar">
        <div className="px-5 h-16 flex items-center border-b border-white/10" data-testid="sidebar-brand">
          <img src="/yousolved-logo.png" alt="Yousolved" className="h-8 w-auto" />
        </div>
        <nav className="flex-1 py-4">
          {NAV.filter(n => level >= n.min).map((n) => (
            <NavLink key={n.to} to={n.to} data-testid={`sidebar-${n.label.toLowerCase()}`}
              className={({ isActive }) => `sidebar-item ${isActive ? "active" : ""} flex items-center gap-3 px-5 py-2.5 text-sm`}>
              <n.icon className="w-4 h-4" />
              <span>{n.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="px-5 py-3 border-t border-white/10 text-xs text-white/50">
          <div className="uppercase tracking-widest mb-1">Environment</div>
          <div className="font-mono">org_alfred_demo</div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="header-dark h-16 px-6 flex items-center justify-between gap-4" data-testid="app-header">
          <div className="flex items-center gap-3 flex-1 max-w-xl">
            <button
              onClick={() => setCmdOpen(true)}
              data-testid="global-search"
              className="relative w-full flex items-center gap-2 pl-9 pr-3 py-2 text-sm rounded-full bg-white/10 border border-white/20 text-white/70 hover:bg-white/15 hover:text-white transition-colors text-left">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/50" />
              <span className="flex-1 truncate">Search jobs, candidates, employees…</span>
              <kbd className="hidden sm:inline-flex items-center gap-1 text-[10px] font-mono px-1.5 py-0.5 rounded border border-white/20 text-white/60 shrink-0">⌘K</kbd>
            </button>
            <Button onClick={() => setAskOpen(true)} data-testid="ask-alfred-btn"
              className="btn-primary rounded-full shrink-0 gap-2 whitespace-nowrap">
              <Mic className="w-4 h-4" /> Ask Alfred
            </Button>
          </div>
          <div className="flex items-center gap-3">
            <NotificationsBell />
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center gap-2 text-white outline-none" data-testid="user-menu">
                <Avatar className="w-8 h-8">
                  <AvatarFallback className="bg-pink text-white text-xs">{initials}</AvatarFallback>
                </Avatar>
                <div className="text-left hidden sm:block">
                  <div className="text-sm font-semibold leading-tight">{user?.name}</div>
                  <div className="text-[10px] uppercase tracking-wider text-white/60">{user?.role?.replace(/_/g," ").toLowerCase()}</div>
                </div>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="text-sm">{user?.name}</div>
                  <div className="text-xs text-neutral-500">{user?.email}</div>
                  <Badge variant="outline" className="mt-1 text-[10px]">{user?.role}</Badge>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild><Link to="/me" data-testid="user-menu-me">My space</Link></DropdownMenuItem>
                <DropdownMenuItem asChild><Link to="/app/settings">Settings</Link></DropdownMenuItem>
                <DropdownMenuItem onClick={doLogout} data-testid="logout-btn"><LogOut className="w-4 h-4 mr-2" /> Sign out</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        <main className="flex-1 p-6 lg:p-10 overflow-y-auto" data-testid="app-main">
          {showRecruitNav && <RecruitSubNav />}
          <NotificationBanner />
          <Outlet />
        </main>
      </div>
      <AlfredPanel open={askOpen} onOpenChange={setAskOpen} />
      <CommandPalette open={cmdOpen} onOpenChange={setCmdOpen} />
    </div>
  );
}
